import { Style } from 'ol/style';

import { memoizeStyleFunction, hexToRGB, cloneStyleValue, convertSize } from './styleUtils';
import { getSimpleStroke } from './simpleStyles';
import getGraphicStrokeStyle from './graphicStrokeStyle';

/**
 * @private
 * @param  {object} symbolizer SLD symbolizer object.
 * @return {object} OpenLayers style instance corresponding to the stroke of the given symbolizer.
 */
function lineStyle(symbolizer) {
    if (symbolizer.stroke && symbolizer.stroke.graphicstroke) {
        return getGraphicStrokeStyle(symbolizer);
    }

    return new Style({
        stroke: getSimpleStroke(symbolizer.stroke),
    });
}

const cachedLineStyle = memoizeStyleFunction(lineStyle);

/**
 * @private
 * Get an OL line style instance for a feature according to a symbolizer.
 * @param {object} symbolizer SLD symbolizer object.
 * @returns {ol/Style} OpenLayers style instance.
 */
function getLineStyle(symbolizer, feature) {
    const olStyle = cloneStyleValue(cachedLineStyle(symbolizer));

    if (feature.get('xstate') == null || feature.get('xstate') == 0) {
        olStyle.setZIndex(0);
    } else {
        olStyle.setZIndex(100);
    }

    const strokeStyling = symbolizer?.stroke?.styling;
    if (strokeStyling && (strokeStyling.stroke || strokeStyling.strokeOpacity || strokeStyling.strokeWidth || strokeStyling.strokeDasharray || strokeStyling.strokeDashoffset)) {
        const strokeValue = resolveValue(strokeStyling.stroke, feature);
        const strokeOpacityValue = resolveValue(strokeStyling.strokeOpacity, feature);
        const strokeWidthValue = resolveValue(strokeStyling.strokeWidth, feature);
        const strokeDasharrayValue = resolveValue(strokeStyling.strokeDasharray, feature);
        const strokeDashoffsetValue = resolveValue(strokeStyling.strokeDashoffset, feature);

        if (strokeValue != null || strokeOpacityValue != null || strokeWidthValue != null || strokeDasharrayValue != null || strokeDashoffsetValue != null) {
            const strokeColor = buildColor(strokeValue, strokeOpacityValue);
            const defaultStrokeWidth = resolveNumericOrDefault(strokeStyling.strokeWidth, 1);
            const resolvedWidth = resolveNumericOrDefault(strokeWidthValue, defaultStrokeWidth);
            const convertedWidth = convertSymbolizerSize(
                resolvedWidth,
                feature,
                symbolizer
            );
            const finalStrokeWidth = Number.isNaN(convertedWidth)
                ? resolvedWidth
                : convertedWidth;
            const resolvedDashOffset = Number(strokeDashoffsetValue);
            const convertedDashOffset = convertSymbolizerSize(
                resolvedDashOffset,
                feature,
                symbolizer
            );
            const hasDashOffset = !Number.isNaN(convertedDashOffset);

            let olStroke = olStyle.getStroke && olStyle.getStroke();
            if (!olStroke) {
                const baseColor =
                    strokeColor != null
                        ? strokeColor
                        : (strokeOpacityValue || strokeOpacityValue === 0)
                            ? hexToRGB('#000000', strokeOpacityValue)
                            : '#000000';
                olStroke = getSimpleStroke({
                    styling: {
                        stroke: baseColor,
                        strokeWidth: finalStrokeWidth != null ? finalStrokeWidth : 1
                    }
                });
                if (olStroke && olStyle.setStroke) {
                    olStyle.setStroke(olStroke);
                }
            }

            if (olStroke) {
                if (strokeColor != null) {
                    olStroke.setColor(strokeColor);
                } else if (strokeOpacityValue || strokeOpacityValue === 0) {
                    const currentColor = olStroke.getColor();
                    if (isHexColor(currentColor)) {
                        olStroke.setColor(hexToRGB(currentColor, strokeOpacityValue));
                    }
                }

                olStroke.setWidth(finalStrokeWidth);

                if (strokeDasharrayValue != null) {
                    const rawLineDash = Array.isArray(strokeDasharrayValue)
                        ? strokeDasharrayValue
                        : String(strokeDasharrayValue).trim().split(/[\s,]+/);
                    const lineDash = rawLineDash
                        .map(entry => {
                            const numericEntry = Number(entry);
                            if (Number.isNaN(numericEntry)) {
                                return null;
                            }
                            const convertedEntry = convertSymbolizerSize(
                                numericEntry,
                                feature,
                                symbolizer
                            );
                            return Number.isNaN(convertedEntry) ? numericEntry : convertedEntry;
                        })
                        .filter(entry => entry != null);
                    olStroke.setLineDash(lineDash);
                }

                if (hasDashOffset) {
                    olStroke.setLineDashOffset(convertedDashOffset);
                }
            }
        }
    }

    return olStyle;
}

function resolveValue(value, feature) {
    if (value && value.prop) {
        return feature.get(value.prop);
    }
    if (value && value.type === 'propertyname') {
        return feature.get(value.value);
    }
    return value;
}

function resolveNumericOrDefault(value, fallback) {
    if (value == null || value === '') {
        return fallback;
    }
    if (typeof value === 'object') {
        return fallback;
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? fallback : numericValue;
}

function convertSymbolizerSize(value, feature, symbolizer) {
    return convertSize(
        Number(value),
        feature.get('currentResolution'),
        feature.get('currentUom'),
        symbolizer && symbolizer.uom
    );
}

function buildColor(value, opacity) {
    if ((opacity || opacity === 0) && isHexColor(value)) {
        return hexToRGB(value, opacity);
    }
    return value;
}

function isHexColor(value) {
    return value && value.slice && value.slice(0, 1) === '#';
}

export default getLineStyle;
